import React, { useEffect, useState } from "react";
import { Box, Paper, Typography, Grid, Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import i18next from "i18next";
import FormInput from "../inputs/FormInput";
import TextEditor from "../inputs/TextEditor";
import LanguageToggle from "../global/LanguageToggle";

const FormHomeHeaderSection = ({ currentData, onSubmit, isLoading }) => {
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng.startsWith("ar");
    const initialLang = localStorage.getItem("i18nextLng") || i18next.language || "ar";
    const [activeLang, setActiveLang] = useState(initialLang.startsWith("ar") ? "ar" : "en");

    const [titleAr, setTitleAr] = useState("");
    const [titleEn, setTitleEn] = useState("");
    const [coloredTitleAr, setColoredTitleAr] = useState("");
    const [coloredTitleEn, setColoredTitleEn] = useState("");
    const [descriptionAr, setDescriptionAr] = useState("");
    const [descriptionEn, setDescriptionEn] = useState("");
    const [image, setImage] = useState(null);

    useEffect(() => {
        if (currentData) {
            setTitleAr(currentData?.title?.ar || "");
            setTitleEn(currentData?.title?.en || "");
            setColoredTitleAr(currentData?.colored_title?.ar || "");
            setColoredTitleEn(currentData?.colored_title?.en || "");
            setDescriptionAr(currentData?.description?.ar || "");
            setDescriptionEn(currentData?.description?.en || "");
            setImage(currentData?.image || null);
        }
    }, [currentData]);

    const getImageName = (imageVal) => {
        if (!imageVal) return "";
        if (typeof imageVal === 'string') {
            return imageVal.substr(imageVal.lastIndexOf('/') + 1);
        }
        return imageVal;
    };

    const handleImageChange = (e) => {
        setImage(e.target.value);
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        const payload = {
            title: {
                ar: titleAr || titleEn,
                en: titleEn || titleAr,
            },
            colored_title: {
                ar: coloredTitleAr || coloredTitleEn,
                en: coloredTitleEn || coloredTitleAr,
            },
            description: {
                ar: descriptionAr || descriptionEn,
                en: descriptionEn || descriptionAr,
            },
            image: getImageName(image),
        };

        onSubmit(payload);
    };

    return (
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
            <Paper sx={{ p: 3, borderRadius: 2 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, flexWrap: "wrap", gap: 2 }}>
                    <Typography variant="h6">
                        {isArabic ? "رأس الصفحة الرئيسية" : "Home Header"}
                    </Typography>
                    <LanguageToggle activeLang={activeLang} onChange={(lng) => setActiveLang(lng)} showLabel={true} />
                </Box>

                <Grid container spacing={3}>
                    <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                        <FormInput
                            type="file"
                            name="image"
                            labelImage={isArabic ? "الصورة" : "Image"}
                            value={image}
                            onChange={handleImageChange}
                            width={200}
                            height={120}
                        />
                    </Grid>

                    {activeLang === "ar" && (
                        <Grid item xs={12} md={6}>
                            <FormInput
                                label="العنوان (عربي)"
                                placeholder="أدخل العنوان بالعربي"
                                value={titleAr}
                                onChange={(e) => setTitleAr(e.target.value)}
                                fullWidth
                                required
                            />
                        </Grid>
                    )}
                    {activeLang === "en" && (
                        <Grid item xs={12} md={6}>
                            <FormInput
                                label="Title (English)"
                                placeholder="Enter title in English"
                                value={titleEn}
                                onChange={(e) => setTitleEn(e.target.value)}
                                fullWidth
                                required
                            />
                        </Grid>
                    )}

                    {activeLang === "ar" && (
                        <Grid item xs={12} md={6}>
                            <FormInput
                                label="العنوان الملون (عربي)"
                                placeholder="أدخل العنوان الملون بالعربي"
                                value={coloredTitleAr}
                                onChange={(e) => setColoredTitleAr(e.target.value)}
                                fullWidth
                            />
                        </Grid>
                    )}
                    {activeLang === "en" && (
                        <Grid item xs={12} md={6}>
                            <FormInput
                                label="Colored Title (English)"
                                placeholder="Enter colored title in English"
                                value={coloredTitleEn}
                                onChange={(e) => setColoredTitleEn(e.target.value)}
                                fullWidth
                            />
                        </Grid>
                    )}

                    {activeLang === "ar" && (
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
                                الوصف (عربي)
                            </Typography>
                            <TextEditor
                                name="description_ar"
                                value={descriptionAr}
                                onChange={(e) => setDescriptionAr(e.target.value)}
                                placeholder="أدخل الوصف بالعربي"
                            />
                        </Grid>
                    )}
                    {activeLang === "en" && (
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
                                Description (English)
                            </Typography>
                            <TextEditor
                                name="description_en"
                                value={descriptionEn}
                                onChange={(e) => setDescriptionEn(e.target.value)}
                                placeholder="Enter description in English"
                            />
                        </Grid>
                    )}
                </Grid>
            </Paper>

            <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                <Button
                    type="submit"
                    variant="contained"
                    disabled={isLoading}
                    sx={{ minWidth: 120 }}
                >
                    {isLoading ? (isArabic ? "جاري الحفظ..." : "Saving...") : (isArabic ? "حفظ" : "Save")}
                </Button>
            </Box>
        </Box>
    );
};

export default FormHomeHeaderSection;
